import { FC, useCallback, useMemo } from 'react';

import { t } from 'state/intl/translations';
import { useUIContext } from 'state/ui';

import { IRadioCheckListOption, ModifierListItem } from './modifier-list-item';
import { IModifierMultiplier, IModifierTypeUIProps } from './types';
import { getSelectedMultiplier, logProductModifierCustomization } from './utils';

export const ModifierCheckbox: FC<IModifierTypeUIProps> = ({
  modifier,
  selections,
  onSelectionsChange,
}) => {
  const { formatCurrencyForLocale } = useUIContext();

  const regularLabel = t('regular');
  const noLabel = t('no');

  const selectedMultiplier = getSelectedMultiplier(modifier, selections);
  const isSelected = selectedMultiplier.multiplier === 1;

  const checkBoxOptions: IRadioCheckListOption[] = useMemo(() => {
    return modifier.multipliers
      .sort((a, b) => a.multiplier - b.multiplier) // Sort from lowest to highest
      .map(multiplier => {
        const isRegular = multiplier.multiplier !== 0;
        const label = isRegular ? regularLabel : noLabel;

        const price = multiplier.price ?? 0;
        return {
          label,
          labelSuffix: price > 0 ? `+${formatCurrencyForLocale(price)}` : '',
          value: multiplier.id,
        };
      });
  }, [formatCurrencyForLocale, modifier, regularLabel, noLabel]);

  const onOptionPress = useCallback(
    (multiplier: IModifierMultiplier, optionValue: string) => {
      onSelectionsChange({ [optionValue]: 1 });
      logProductModifierCustomization(multiplier.name, modifier.name, optionValue);
    },
    [onSelectionsChange, modifier]
  );

  return (
    <ModifierListItem
      isSelected={isSelected}
      badgeContent={isSelected ? regularLabel : ''}
      content={modifier}
      selectedOption={selectedMultiplier.id}
      itemValue={selectedMultiplier}
      options={checkBoxOptions}
      onOptionPress={onOptionPress}
    />
  );
};
